import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaFacebook, FaInstagram, FaLinkedin, FaPlus, FaTrash, FaCheckCircle, FaMusic, FaYoutube, FaTimes, FaLink, FaArrowRight } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { accountsAPI } from '../utils/api';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';

const platforms = [
  { id: 'twitter', name: 'X', Icon: FaXTwitter, color: '#000000', desc: 'Share points as posts on X' },
  { id: 'facebook', name: 'Facebook', Icon: FaFacebook, color: '#1877F2', desc: 'Post to your timeline' },
  { id: 'instagram', name: 'Instagram', Icon: FaInstagram, color: '#E4405F', desc: 'Share photos & reels' },
  { id: 'linkedin', name: 'LinkedIn', Icon: FaLinkedin, color: '#0A66C2', desc: 'Professional updates' },
  { id: 'tiktok', name: 'TikTok', Icon: FaMusic, color: '#010101', desc: 'Cross-post PointClips' },
  { id: 'youtube', name: 'YouTube', Icon: FaYoutube, color: '#FF0000', desc: 'Upload longer content' },
];

const Connections = () => {
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [username, setUsername] = useState('');

  useEffect(() => { loadConnectedAccounts(); }, []);

  const loadConnectedAccounts = async () => {
    try {
      const res = await accountsAPI.getConnected();
      setConnectedAccounts(res.data);
    } catch (err) { console.error('Failed to load connected accounts', err); } finally { setLoading(false); }
  };

  const handleConnect = async (e) => {
    e.preventDefault();
    try {
      await accountsAPI.connect({ platform: selectedPlatform.id, platform_username: username });
      toast.success(`${selectedPlatform.name} connected!`);
      loadConnectedAccounts();
      setShowModal(false);
      setSelectedPlatform(null);
      setUsername('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to connect');
    }
  };

  const handleDisconnect = async (account) => {
    try {
      await accountsAPI.disconnect(account.id);
      toast.success('Disconnected');
      loadConnectedAccounts();
    } catch { toast.error('Failed to disconnect'); }
  };

  const isConnected = (id) => connectedAccounts.find(a => a.platform === id);
  const connectedCount = connectedAccounts.length;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="connections-page">
      <div className="grid grid-cols-1 md:grid-cols-12">
        <Sidebar />
        <main className="col-span-12 md:col-span-10">
          {/* Hero Section */}
          <div className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#23cda0] via-[#2263f2] to-[#6c30ad]" />
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
            <div className="relative px-6 md:px-10 py-10">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <div className="flex items-center space-x-3 mb-2">
                  <FaLink className="text-white/80" size={20} />
                  <h1 className="text-3xl font-outfit font-bold text-white tracking-tight" data-testid="connections-title">Connections</h1>
                </div>
                <p className="text-white/70 text-sm max-w-md">
                  Link your social accounts to cross-post content from TalkinPoint everywhere at once.
                </p>
                <div className="flex items-center space-x-6 mt-5">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2.5 border border-white/10">
                    <p className="text-white/50 text-[10px] font-bold uppercase tracking-wider">Connected</p>
                    <p className="text-white text-2xl font-bold">{connectedCount}</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2.5 border border-white/10">
                    <p className="text-white/50 text-[10px] font-bold uppercase tracking-wider">Available</p>
                    <p className="text-white text-2xl font-bold">{platforms.length - connectedCount}</p>
                  </div>
                  <div className="hidden sm:flex items-center space-x-1.5">
                    {platforms.map(p => {
                      const conn = isConnected(p.id);
                      return (
                        <div
                          key={p.id}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                            conn ? 'bg-white/20' : 'bg-white/5'
                          }`}
                          title={`${p.name}${conn ? ' (connected)' : ''}`}
                        >
                          <p.Icon size={14} className={conn ? 'text-white' : 'text-white/30'} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Platform Cards */}
          <div className="px-6 md:px-10 py-8">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={`skeleton-${i}`} className="bg-white rounded-2xl h-48 animate-pulse border border-gray-100" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {platforms.map((platform, index) => {
                  const account = isConnected(platform.id);
                  return (
                    <motion.div
                      key={platform.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.06 }}
                      className={`group relative bg-white rounded-2xl overflow-hidden border transition-all hover:shadow-lg ${
                        account ? 'border-[#23cda0]/30 shadow-sm' : 'border-gray-100 hover:border-gray-200'
                      }`}
                      data-testid={`platform-${platform.id}`}
                    >
                      {/* Top color stripe */}
                      <div className="h-1" style={{ background: account ? `linear-gradient(90deg, ${platform.color}, ${platform.color}88)` : '#e5e7eb' }} />

                      <div className="p-5">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div
                              className="w-12 h-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
                              style={{ backgroundColor: `${platform.color}12` }}
                            >
                              <platform.Icon size={22} style={{ color: platform.color }} />
                            </div>
                            <div>
                              <h3 className="text-gray-900 font-bold text-base">{platform.name}</h3>
                              <p className="text-gray-400 text-xs">{platform.desc}</p>
                            </div>
                          </div>
                          {account && (
                            <div className="flex items-center space-x-1 bg-[#23cda0]/10 text-[#23cda0] px-2 py-1 rounded-full">
                              <FaCheckCircle size={10} />
                              <span className="text-[10px] font-bold uppercase tracking-wider">Linked</span>
                            </div>
                          )}
                        </div>

                        {account ? (
                          <div>
                            <div className="bg-gray-50 rounded-xl px-3 py-2.5 mb-3 flex items-center justify-between">
                              <div>
                                <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Username</p>
                                <p className="text-gray-900 font-semibold text-sm">@{account.platform_username}</p>
                              </div>
                              <div className="w-2 h-2 rounded-full bg-[#23cda0] animate-pulse" title="Active" />
                            </div>
                            <button
                              data-testid={`disconnect-${platform.id}`}
                              onClick={() => handleDisconnect(account)}
                              className="w-full bg-gray-50 text-gray-400 hover:bg-red-50 hover:text-red-500 rounded-xl py-2.5 text-xs font-medium transition-all flex items-center justify-center space-x-1.5 border border-transparent hover:border-red-100"
                            >
                              <FaTrash size={10} />
                              <span>Disconnect</span>
                            </button>
                          </div>
                        ) : (
                          <button
                            data-testid={`connect-${platform.id}`}
                            onClick={() => { setSelectedPlatform(platform); setShowModal(true); }}
                            className="w-full bg-gray-900 text-white hover:bg-gray-800 rounded-xl py-3 text-sm font-semibold transition-all flex items-center justify-center space-x-2 group/btn"
                          >
                            <FaPlus size={10} />
                            <span>Connect</span>
                            <FaArrowRight size={10} className="opacity-0 -translate-x-1 group-hover/btn:opacity-100 group-hover/btn:translate-x-0 transition-all" />
                          </button>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {/* How it works */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="mt-8 bg-white border border-gray-100 rounded-2xl p-6"
            >
              <h2 className="text-lg font-outfit font-bold text-gray-900 mb-4">How Cross-Posting Works</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { step: '1', title: 'Connect', desc: 'Link your social accounts with your username' },
                  { step: '2', title: 'Create', desc: 'Make a Point and select platforms to share' },
                  { step: '3', title: 'Distribute', desc: 'Your content goes live everywhere at once' },
                ].map(item => (
                  <div key={item.step} className="flex items-start space-x-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#23cda0] to-[#2263f2] flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">{item.step}</span>
                    </div>
                    <div>
                      <p className="text-gray-900 font-semibold text-sm">{item.title}</p>
                      <p className="text-gray-400 text-xs mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </main>
      </div>

      {/* Connect Modal */}
      <AnimatePresence>
        {showModal && selectedPlatform && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => { setShowModal(false); setSelectedPlatform(null); setUsername(''); }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden"
              data-testid="connect-modal"
            >
              {/* Modal header with platform color */}
              <div className="p-6 text-center" style={{ background: `linear-gradient(135deg, ${selectedPlatform.color}15, ${selectedPlatform.color}05)` }}>
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-3"
                  style={{ backgroundColor: `${selectedPlatform.color}15` }}
                >
                  <selectedPlatform.Icon size={26} style={{ color: selectedPlatform.color }} />
                </div>
                <h2 className="text-xl font-outfit font-bold text-gray-900">
                  Connect {selectedPlatform.name}
                </h2>
                <p className="text-gray-400 text-xs mt-1">{selectedPlatform.desc}</p>
              </div>

              <form onSubmit={handleConnect} className="p-6 pt-4 space-y-4">
                <div>
                  <label className="text-xs text-gray-500 font-medium mb-1.5 block">Your {selectedPlatform.name} username</label>
                  <input
                    data-testid="connect-username-input"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 focus:border-gray-900 focus:ring-1 focus:ring-gray-900/10 rounded-xl h-11 px-4 text-sm text-gray-900 placeholder:text-gray-300 transition-all outline-none"
                    placeholder="@username"
                    required
                    autoFocus
                  />
                </div>
                <div className="flex space-x-3 pt-1">
                  <button
                    type="button"
                    onClick={() => { setShowModal(false); setSelectedPlatform(null); setUsername(''); }}
                    className="flex-1 bg-gray-100 text-gray-600 hover:bg-gray-200 rounded-xl py-2.5 text-sm font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    data-testid="connect-submit-btn"
                    type="submit"
                    className="flex-1 bg-gray-900 text-white hover:bg-gray-800 rounded-xl py-2.5 text-sm font-bold transition-colors"
                  >
                    Connect
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Connections;
